import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Stack,
  CircularProgress,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import {
  TrendingUp,
  School,
  EmojiEvents,
  DonutLarge,
  Timeline,
} from "@mui/icons-material";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import Header from "../Components/Header";
import StudentSidebar from "./StudentSdebar";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const URL = "https://tdtlworld.com/dss-kaas-backend/";

  const [statsData, setStatsData] = useState([]);
  const [graphData, setGraph] = useState([]);
  const [pieData, setPie] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const COLORS = [
    "#D4AF37", // Gold
    "#3953A3", // Blue
    "#7D52A0", // Purple
    "#B8529E", // Pink
    "#f4811e", // Orange
    "#00C49F", // Teal
    "#FF8042", // Coral
    "#2C73D2", // Azure
  ];

  useEffect(() => {
    const token = localStorage.getItem("jwttoken");
    if (!token) {
      navigate("/db-shiksha/kaas/login");
      return;
    }

    axios
      .get(`${URL}lms/dashboard/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setGraph(response.data.graph_data || []);
        setStatsData(response.data.metrics || []);
        setPie(response.data.pie_data || []);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  }, [navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Header />
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? "calc(100% - 260px)" : "calc(100% - 80px)",
            transition: theme.transitions.create("width", { duration: 300 }),
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress
                size={60}
                thickness={4}
                sx={{ color: "#D4AF37" }}
              />
            </Box>
          ) : (
            <Container maxWidth="xl">
              {/* Welcome Header */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  gutterBottom
                  color="#D4AF37"
                >
                  Welcome back, Student!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Here's what's happening with your courses and progress today.
                </Typography>
              </Box>

              {/* Quick Action Tiles */}
              <Grid container spacing={3} sx={{ mb: 6 }}>
                {[
                  {
                    label: "Grades A+",
                    icon: <TrendingUp />,
                    color: "#D4AF37",
                  },
                  {
                    label: "Achievements",
                    icon: <EmojiEvents />,
                    color: "#B8529E",
                  },
                  {
                    label: "Course Basics",
                    icon: <School />,
                    color: "#3953A3",
                  },
                ].map((item, idx) => (
                  <Grid item xs={12} sm={4} key={idx}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: "20px",
                        bgcolor: alpha(item.color, 0.1),
                        border: "1px solid",
                        borderColor: alpha(item.color, 0.2),
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        transition: "0.3s",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          bgcolor: alpha(item.color, 0.15),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "15px",
                          bgcolor: item.color,
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        color={item.color}
                      >
                        {item.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Stats Grid */}
              <Grid container spacing={3} sx={{ mb: 6 }}>
                {statsData.map((data, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card
                      sx={{
                        borderRadius: "24px",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                        transition: "0.3s",
                        "&:hover": { transform: "scale(1.02)" },
                      }}
                    >
                      <CardContent sx={{ p: 4, textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          fontWeight={900}
                          color="primary"
                          sx={{ mb: 1 }}
                        >
                          {data.value}
                        </Typography>
                        <Divider
                          sx={{
                            mb: 2,
                            width: "40px",
                            mx: "auto",
                            borderWidth: 2,
                            borderColor: "primary.main",
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          color="text.secondary"
                        >
                          {data.label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Charts Selection */}
              <Grid container spacing={4}>
                {/* Popular Courses Pie Chart */}
                <Grid item xs={12} lg={6}>
                  <Paper
                    sx={{
                      p: 4,
                      borderRadius: "24px",
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      sx={{
                        mb: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <DonutLarge /> Popular Courses
                    </Typography>
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip />
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={110}
                            label
                          >
                            {pieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid>

                {/* Monthly Activity Bar Chart */}
                <Grid item xs={12} lg={6}>
                  <Paper
                    sx={{
                      p: 4,
                      borderRadius: "24px",
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      sx={{
                        mb: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Timeline /> Learning Activity
                    </Typography>
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={graphData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke={alpha(theme.palette.divider, 0.5)}
                          />
                          <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fontWeight: 600 }}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fontWeight: 600 }}
                          />
                          <Tooltip
                            contentStyle={{
                              borderRadius: "12px",
                              border: "none",
                              boxShadow: theme.shadows[3],
                            }}
                          />
                          <Bar
                            dataKey="value"
                            fill={theme.palette.primary.main}
                            radius={[10, 10, 0, 0]}
                            barSize={40}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
